import { useState, useEffect } from 'react';
import React from 'react';
import { AgentInfo, AgentType, AgentCategory, AgentFromApi } from '../types';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { getAccessToken } from '../utils/authStorage';
import { getIconComponent } from '../utils/iconMapper';
import { HomeIcon, BookOpenIcon, BeakerIcon } from '@heroicons/react/24/outline';

// Mapeamento de package_code para AgentCategory
// Baseado nos package_codes reais do banco de dados
const PACKAGE_TO_CATEGORY: Record<string, AgentCategory | undefined> = {
  'investigar-textos': AgentCategory.INVESTIGATING_TEXTS,
  'mapear-temas': AgentCategory.MAPPING_THEMES,
  'explorar-significados': AgentCategory.EXPLORING_MEANINGS,
  'preparar-mensagens': AgentCategory.PREPARING_MESSAGES,
  'devocional': undefined, // Sem categoria
  // Fallbacks para possíveis variações
  'exegese': AgentCategory.INVESTIGATING_TEXTS,
  'teologia_biblica': AgentCategory.MAPPING_THEMES,
  'teologia_sistematica': AgentCategory.EXPLORING_MEANINGS,
  'homiletica': AgentCategory.PREPARING_MESSAGES,
};

// Mapeamento de códigos do backend para AgentType
// Mapeia códigos (ex: 'analisar-palavras') para valores do enum AgentType
const CODE_TO_AGENT_TYPE: Record<string, AgentType> = {
  'devocional-pessoal': AgentType.DEVOCIONAL_PESSOAL,
  'analisar-palavras': AgentType.WORD_ANALYZER,
  'caracterizar-personagens': AgentType.CHARACTERS,
  'comparar-traducoes': AgentType.TRANSLATION_COMPARATOR,
  'delimitar-unidades': AgentType.BIBLICAL_UNIT_IDENTIFIER,
  'identificar-forma-literaria': AgentType.LITERARY_FORM,
  'pesquisar-contexto': AgentType.CONTEXT_RESEARCHER,
  'construir-sermao': AgentType.SERMON_BUILDER,
  'gerar-ilustracoes': AgentType.ILLUSTRATION_GENERATOR,
  'definir-publico-alvo': AgentType.TARGET_AUDIENCE_DEFINER,
  'preparar-aula': AgentType.LESSON_PREPARER,
  'sugerir-posts': AgentType.POST_CREATOR,
  'atualizar-texto': AgentType.TEXT_UPDATER,
  'examinar-cosmovisao': AgentType.WORLDVIEW_ANALYZER,
  'historiar-interpretacao': AgentType.INTERPRETATION_HISTORIAN,
  'descrever-teologias': AgentType.DESCRIBE_THEOLOGIES,
  'explorar-temas': AgentType.EXPLORE_THEMES,
  'biografar-personagens': AgentType.BIOGRAPH_CHARACTERS,
  'relacionar-textos': AgentType.RELATE_TEXTS,
  'apresentar-teologia-livros': AgentType.GENERATE_BOOK_THEOLOGY,
  'criar-estudo-biblico': AgentType.BIBLE_STUDY_CREATOR,
};

// Função auxiliar para mapear código para AgentType
function mapCodeToAgentType(code: string): AgentType {
  return CODE_TO_AGENT_TYPE[code] || (code as AgentType);
}

// Agentes especiais que sempre devem aparecer (não vêm da API)
const SPECIAL_AGENTS: AgentInfo[] = [
  {
    id: AgentType.WELCOME,
    name: 'MENU',
    description: 'Página inicial de boas-vindas.',
    icon: React.createElement(HomeIcon, { className: 'w-6 h-6' }),
    category: undefined,
  },
];

export interface UseAgentsResult {
  agents: AgentInfo[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAgents(): UseAgentsResult {
  const [agents, setAgents] = useState<AgentInfo[]>(SPECIAL_AGENTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAccessToken();
      const url = `${API_CONFIG.BASE_URL}/api/agents/`;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useAgents] Erro na resposta:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // A resposta pode ser um array direto ou um objeto com propriedade 'results' (paginação DRF)
      let agentsList: AgentFromApi[] = [];
      if (Array.isArray(data)) {
        agentsList = data as AgentFromApi[];
      } else if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        // Django REST Framework retorna paginado: {count, next, previous, results: [...]}
        if (Array.isArray(obj.results)) {
          agentsList = obj.results as AgentFromApi[];
        } else if (Array.isArray(obj.data)) {
          agentsList = obj.data as AgentFromApi[];
        } else if (Array.isArray(obj.agents)) {
          agentsList = obj.agents as AgentFromApi[];
        }
      }
      
      // Transformar agentes da API para o formato AgentInfo
      const apiAgents: AgentInfo[] = agentsList
        .filter((agent) => agent.status === 'active')
        .map((agent) => {
          const category = PACKAGE_TO_CATEGORY[agent.package_code];
          // Mapear código do backend para AgentType
          const agentType = mapCodeToAgentType(agent.code);
          
          // Override de ícones específicos para garantir consistência
          let iconOverride: React.ReactNode | null = null;
          if (agentType === AgentType.DEVOCIONAL_PESSOAL) {
            iconOverride = React.createElement(BookOpenIcon, { className: 'w-6 h-6' });
          } else if (agentType === AgentType.WORD_ANALYZER) {
            iconOverride = React.createElement(BeakerIcon, { className: 'w-6 h-6' });
          }
          
          // Nome exibido: override para TEXT_UPDATER (menu deve mostrar "Atualizar Textos")
          const displayName = agentType === AgentType.TEXT_UPDATER ? 'Atualizar Textos' : agent.name;

          return {
            id: agentType,
            name: displayName,
            description: agent.short_description || agent.description || '',
            icon: iconOverride || getIconComponent(agent.icon),
            category: category,
          };
        });

      // Combinar agentes especiais com agentes da API
      const allAgents = [...SPECIAL_AGENTS, ...apiAgents];
      setAgents(allAgents);
    } catch (err) {
      console.error('[useAgents] Erro ao buscar agentes:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar agentes');
      // Em caso de erro, manter apenas agentes especiais
      setAgents(SPECIAL_AGENTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAgents();
    } else {
      // Se não autenticado, usar apenas agentes especiais
      setAgents(SPECIAL_AGENTS);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return {
    agents,
    loading,
    error,
    refresh: fetchAgents,
  };
}

